'use client'
import React, { useState, useCallback } from 'react'
import { useRouter } from 'next/navigation'

interface InteractiveRegion {
  id: string
  name: string
  shapeType: 'rect' | 'polygon' | 'circle'
  x: number
  y: number
  width: number
  height: number
  points?: Array<{ x: number; y: number }>
  linkedEventId: string | null
  linkedEventSlug?: string | null
  linkedUrl: string | null
  hoverColor: string
  borderColor: string
  labelColor: string
  sortOrder: number
  isActive: boolean
  alwaysHighlight?: boolean
}

interface LinkedEvent {
  id: string
  name: string
  slug: string
  status: string
  startDate?: string
  endDate?: string
  imageUrl?: string | null
  eventType?: string | null
}

interface VenueMapViewerProps {
  imageUrl: string
  regions: InteractiveRegion[]
  events: LinkedEvent[]
  className?: string
}

export default function VenueMapViewer({ imageUrl, regions, events, className = '' }: VenueMapViewerProps) {
  const router = useRouter()
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [tappedId, setTappedId] = useState<string | null>(null)
  const [imageError, setImageError] = useState(false)

  const getEvent = useCallback((eventId: string | null) => {
    if (!eventId) return null
    return events.find(e => e.id === eventId) || null
  }, [events])

  const handleRegionClick = useCallback((region: InteractiveRegion) => {
    // Mobile: first tap shows label, second tap navigates
    if ('ontouchstart' in window) {
      if (tappedId !== region.id) {
        setTappedId(region.id)
        return
      }
    }

    const event = getEvent(region.linkedEventId)
    const slug = event?.slug || region.linkedEventSlug
    if (slug) {
      router.push(`/events/${slug}`)
    } else if (region.linkedUrl) {
      try {
        const url = new URL(region.linkedUrl)
        if (['http:', 'https:'].includes(url.protocol)) {
          window.open(region.linkedUrl, '_blank', 'noopener')
        }
      } catch {
        // Invalid URL, ignore
      }
    }
  }, [tappedId, getEvent, router])

  const activeRegions = regions
    .filter(r => r.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder)

  if (imageError) {
    return (
      <div className={`flex items-center justify-center bg-slate-100 rounded-xl p-12 ${className}`}>
        <p className="text-sm text-slate-500">Failed to load map image</p>
      </div>
    )
  }

  return (
    <div className={`relative inline-block w-full ${className}`}>
      <img
        src={imageUrl}
        alt="Event map"
        className="w-full h-auto block rounded-xl"
        draggable={false}
        onError={() => setImageError(true)}
      />

      {/* SVG overlay for all interactive regions */}
      <svg
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="rounded-xl"
      >
        <defs>
          <filter id="region-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          {/* Soft feathered fill — large blur for a smooth color wash */}
          <filter id="region-feather" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="2.5" />
          </filter>
        </defs>

        {activeRegions.map(region => {
          const event = getEvent(region.linkedEventId)
          const isHovered = hoveredId === region.id
          const isTapped = tappedId === region.id
          const isHighlighted = isHovered || isTapped
          const hasLink = !!event?.slug || !!region.linkedEventSlug || !!region.linkedUrl
          const isSoldOut = event?.status === 'SOLD_OUT'

          // Always-highlight uses soft feathered wash; hover uses crisp glow
          const isFeathered = region.alwaysHighlight && !isHighlighted && !isSoldOut
          const fill = isSoldOut
            ? 'rgba(107,114,128,0.4)'
            : isHighlighted
              ? region.hoverColor
              : region.alwaysHighlight
                ? region.hoverColor
                : 'rgba(255,255,255,0.05)'
          const stroke = isFeathered
            ? 'none'
            : isHighlighted
              ? (isSoldOut ? '#6B7280' : region.borderColor)
              : 'rgba(255,255,255,0.1)'

          const sharedProps = {
            fill,
            stroke,
            strokeWidth: isHighlighted ? 2 : 1,
            vectorEffect: 'non-scaling-stroke' as const,
            filter: isSoldOut ? undefined : isFeathered ? 'url(#region-feather)' : isHighlighted ? 'url(#region-glow)' : undefined,
            style: {
              cursor: hasLink && !isSoldOut ? 'pointer' : 'default',
              transition: 'fill 0.3s ease, stroke 0.3s ease, stroke-width 0.25s ease, filter 0.3s ease',
            } as React.CSSProperties,
            onMouseEnter: () => setHoveredId(region.id),
            onMouseLeave: () => {
              setHoveredId(null)
              if (!('ontouchstart' in window)) setTappedId(null)
            },
            onFocus: () => setHoveredId(region.id),
            onBlur: () => setHoveredId(null),
            onClick: () => !isSoldOut && hasLink && handleRegionClick(region),
            onKeyDown: (e: React.KeyboardEvent) => {
              if ((e.key === 'Enter' || e.key === ' ') && hasLink && !isSoldOut) {
                e.preventDefault()
                handleRegionClick(region)
              }
            },
            role: hasLink ? 'link' as const : undefined,
            tabIndex: hasLink ? 0 : undefined,
            'aria-label': `${region.name}${event ? ` - ${event.name}` : ''}${isSoldOut ? ' (Sold Out)' : ''}`,
            className: 'outline-none',
          }

          if (region.shapeType === 'polygon' && region.points) {
            const pointsStr = region.points.map(p => `${p.x},${p.y}`).join(' ')
            return <polygon key={region.id} points={pointsStr} {...sharedProps} />
          }
          if (region.shapeType === 'circle') {
            return (
              <ellipse
                key={region.id}
                cx={region.x + region.width / 2}
                cy={region.y + region.height / 2}
                rx={region.width / 2}
                ry={region.height / 2}
                {...sharedProps}
              />
            )
          }
          return (
            <rect
              key={region.id}
              x={region.x}
              y={region.y}
              width={region.width}
              height={region.height}
              rx={0.6}
              {...sharedProps}
            />
          )
        })}
      </svg>

      {/* HTML label overlays (positioned at bounding box centers) */}
      {activeRegions.map(region => {
        const event = getEvent(region.linkedEventId)
        const isHovered = hoveredId === region.id
        const isTapped = tappedId === region.id
        const isHighlighted = isHovered || isTapped
        const isSoldOut = event?.status === 'SOLD_OUT'
        const hasLabel = region.name.trim() || event || isSoldOut
        if (!hasLabel) return null

        return (
          <div
            key={`label-${region.id}`}
            className="pointer-events-none"
            style={{
              position: 'absolute',
              left: `${region.x + region.width / 2}%`,
              top: `${region.y + region.height / 2}%`,
              transform: `translate(-50%, ${isHighlighted ? '-50%' : '-40%'})`,
              opacity: isHighlighted ? 1 : region.alwaysHighlight ? 0.7 : 0,
              transition: 'opacity 0.25s ease, transform 0.3s ease',
              zIndex: 10,
            }}
          >
            <div className="bg-black/75 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-lg">
              {region.name.trim() && (
                <p
                  className="text-sm font-semibold whitespace-nowrap overflow-hidden text-ellipsis"
                  style={{ color: region.labelColor }}
                >
                  {region.name}
                </p>
              )}
              {event && !isSoldOut && (
                <p className="text-xs text-white/70 whitespace-nowrap overflow-hidden text-ellipsis">
                  {event.name}
                </p>
              )}
              {isSoldOut && (
                <p className="text-xs text-red-300 font-medium">Sold Out</p>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
